import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Diagram } from '@hoopify/shared';
import { CourtSvg } from './Court';

/**
 * Shows a diagram's steps: side by side on wider screens, one at a time with
 * arrows on phones (or always, with `compact`).
 */
export function DiagramView({ diagram, compact }: { diagram: Diagram; compact?: boolean }) {
  const [i, setI] = useState(0);
  const steps = diagram.steps;
  const many = steps.length > 1;
  const cols = diagram.court === 'full' ? 'sm:grid-cols-2 lg:grid-cols-4' : steps.length >= 3 ? 'sm:grid-cols-2 xl:grid-cols-4' : 'sm:grid-cols-2';

  const one = (n: number) => {
    const s = steps[n]!;
    return (
      <figure key={s.id} className="min-w-0">
        <CourtSvg court={diagram.court} objects={s.objects} className="w-full rounded-md" label={`Diagram${many ? `, step ${n + 1}` : ''}${s.caption ? `: ${s.caption}` : ''}`} />
        {(many || s.caption) && (
          <figcaption className="mt-1 text-[13px] text-muted">
            {many && <span className="font-semibold text-ink">Step {n + 1}{s.caption ? ': ' : ''}</span>}
            {s.caption}
          </figcaption>
        )}
      </figure>
    );
  };

  const sizeCap = diagram.court === 'full' ? 'max-w-[220px]' : 'max-w-[320px]';
  if (!many) return <div className={sizeCap}>{one(0)}</div>;

  return (
    <>
      {/* One at a time */}
      <div className={`${compact ? '' : 'sm:hidden'} ${sizeCap}`}>
        {one(i)}
        <div className="mt-1.5 flex items-center gap-2">
          <button className="btn-secondary px-2 py-1" onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0} aria-label="Previous step">
            <ChevronLeft className="size-4" />
          </button>
          <span className="tnum text-[13px] text-muted">
            {i + 1} of {steps.length}
          </span>
          <button className="btn-secondary px-2 py-1" onClick={() => setI(Math.min(steps.length - 1, i + 1))} disabled={i === steps.length - 1} aria-label="Next step">
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>
      {/* All steps side by side */}
      {!compact && <div className={`hidden gap-3 sm:grid ${cols}`}>{steps.map((_, n) => one(n))}</div>}
    </>
  );
}
